import { logError } from "@lib/logger";
import { MockEntityObjectCreator } from "@/testing";
import type { IEventDataSource } from "@/types/interfaces";
import { BigBashEvent } from "@/types/types";
import EventModifier from "@/utils/EventModifier";
import { backBtnStyle } from "@lib/sharedStyles";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("create-event-page")
export class CreateEventPage extends LitElement {
	static readonly styles = [backBtnStyle, css`
        :host {
            display: block;
        }

        .container {
            max-width: 1200px;
            min-width: 1000px;
            border: var(--border-default);
            border-radius: var(--border-radius);
            margin: 0 auto;
            background: white;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            padding: 16px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
            justify-content: center;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            width: 100%;
        }
        
        .header__text {
            display: flex;
            flex-direction: column;
        }
        
        h1 {
            font-family: var(--font-headline);
            font-size: 2rem;
            color: black;
            margin: 0 0;
        }
        
        .default-badge {
            background: var(--color-sand);
            padding: 8px 15px;
            border-radius: 20px;
            font-size: 0.9rem;
            font-family: var(--font-text);
            height: 100%;
        }
    `];

	@state() private event: BigBashEvent | null = null;
	@state() private isLoading: boolean = false;
	@property() eventToBeUpdatedID: string | null = null;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	@property({ type: Object }) eventModifier: EventModifier;

	mockEntityObjectCreator = new MockEntityObjectCreator();

	constructor(eventModifier: EventModifier, eventDataSource?: IEventDataSource) {
		super();
		this.eventModifier = eventModifier;
		this.eventDataSource = eventDataSource;
	}

	async connectedCallback(): Promise<void> {
		super.connectedCallback();
		if (this.eventToBeUpdatedID) {
			this.event = await this._tryToLoadEvent();
		} else {
			this.event = await this._tryToCreateNewEvent();
		}
	}

	private async _tryToCreateNewEvent(): Promise<BigBashEvent> {
		this.isLoading = true;
		try {
			return await this.eventModifier.createEvent();
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private async _tryToLoadEvent(): Promise<BigBashEvent> {
		this.isLoading = true;
		try {
			return await this.eventDataSource.getEventById(this.eventToBeUpdatedID);
		} catch (error) {
			this._handleFetchError(error);
		} finally {
			this.isLoading = false;
		}
	}

	private _handleFetchError(error: unknown): void {
		logError("Error fetching event:", error);
		alert("Failed to load event. Please try again later.");
	}

	private readonly handleBackClick = (event: BigBashEvent) => {
		window.history.pushState({}, '', '/host/events');
	};

	render() {
		if (this.isLoading) {
			return html`
				<div class="container">
					<div style="text-align: center; padding: 60px;">
						Loading event...
					</div>
				</div>
			`;
		}

		return html`
            <a href="/host/events" class="back-btn" @click="${this.handleBackClick}">←</a>
            <div class="container">
                <div class="header">
                    <div class="header__text">
                        <h1>Event erstellen oder bearbeiten</h1>
                    </div>
                    <div class="default-badge">${this.eventToBeUpdatedID ? this.eventToBeUpdatedID : this.event?.id}</div>
                </div>
                <event-form .isUpdate="${!!this.eventToBeUpdatedID}" .eventDataSource="${this.eventDataSource}" .event="${this.event}" style="width: 100%;"></event-form>
            </div>
        `;
	}
}

export default CreateEventPage;